import {
  BannerWrapper,
  CatchupProgramSeries,
  DynamicColumnsWrapper,
  LeftColumn,
  MainWrapper,
  PlayerWrapper,
  ShowMoreButton,
  ShowMoreButtonText,
} from './CatchupPlayer.style'
import { PlayerPropsState } from '../LiveChanelPlayer/LiveChanelPlayer'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ILightRailItem, railItemToLight } from '../../../types'
import { fetchSimilarPrograms } from '../API/API'
import { useOttOverlay } from '../../../contexts'
import { TabsNavItem, TabsNavWrapper } from '../ProgramItem/ProgramItem.style'
import { TopBanner } from '../../../components/Ads'
import { isMobile } from '../../../client'
import { useOttStore } from '../../store/ottStore'
import { prettyLog } from '../../../utils'
import { fetchRail } from '../../Api'
import { OttTaboolaClipPageLeftRailWidget } from '../../AdsComponents'
import { ProgramSeriesClip } from '../ProgramItem/ProgramSeriesClip'
import { he } from '../../../locales/he'
import { ShowMoreArrow } from '../../icons'
import { BreadCrumbs, ICrumb } from '../BreadCrumbs/BreadCrumbs'
import { ProgramSeries } from '../ProgramItem/ProgramSeries'
import {
  CommentItemDateBellowPlayer,
  CommentItemTextBellowPlayer,
  MainRailCommits,
  NameItemTextBellowPlayer,
  RailFirstItemDescriptionBellowPlayer,
} from '../MainProgramMenu/MainProgramMenu.style'
import { OttEditMe } from '../../../sharedComponents'
import { BlockedAdsImage } from '../BlockAds'
import { OTT_DEFAULT_IMAGE, sizedOttImage } from '../../ottImage'
import { useAdBlockStore } from '../../../store'
import dynamic from 'next/dynamic'

const Player = dynamic(() => import('../Player/Player'), { ssr: false })

const ITEMS_PER_ROW = 10
const ADDITIONAL_ROWS = 3

const formatDate = (timestamp: string | number): string => {
  const date = new Date(Number(timestamp) * 1000) // convert seconds to ms
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear())
  return `${day}.${month}.${year}`
}
export const CatchupPlayer = ({
  MEDIAID,
  playerProps,
}: {
  MEDIAID: string
  playerProps: PlayerPropsState
}) => {
  const PlayerWrapperRef = useRef<HTMLDivElement>(null)
  const { ks } = useOttOverlay()
  // AdBlock Decision Engine result (RESH13-1075)
  const adDecision = useAdBlockStore(state => state.decision)
  const taboolaCounter = useRef(0)
  const [seasonsEpisodes, setSeasonsEpisodes] = useState<
    ILightRailItem[] | null
  >(null)

  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false)
  const [videoRailId, setVideoRailId] = useState<string | number>('')
  const { setEpisodeDescription, setAdditionalEpisodes, additionalEpisodes } =
    useOttStore()
  const [selectedNav, setSelectedNav] = useState<'additional' | 'similar'>(
    'additional'
  )

  const [visibleRows, setVisibleRows] = useState(1)
  const [taboolaWidgetCounter, setTaboolaWidgetCounter] = useState<number>(0)
  const [poster, setPoster] = useState('')
  const [additionalDataFetched, setAdditionalDataFetched] =
    useState<boolean>(false)

  useEffect(() => {
    setIsMobileDevice(isMobile())
  }, [])

  useEffect(() => {
    if (!MEDIAID) {
      return
    }
    try {
      fetch('/cdn/video_rail_map.json')
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error ${res.statusText}`)
          }
          return res.json()
        })
        .then(res => {
          const data: { videoId: string; railId: string }[] = res
          const match = data.find(entry => entry.videoId == MEDIAID) || {
            railId: 361965,
          }
          setVideoRailId(match.railId)
        })
        .catch(err => {
          prettyLog('CatchupPlayer: 101', { MEDIAID, err }, 'warning')
          setSelectedNav('similar')
        })
    } catch (err) {
      prettyLog('Error during fetchRailId:', err)
      setSelectedNav('similar')
    }
  }, [MEDIAID])

  useMemo(() => {
    if (playerProps.isTrailer || playerProps.isMovie) {
      if (videoRailId) {
        try {
          if (!additionalDataFetched) {
            fetchRail({
              railId: videoRailId,
              ks,
            }).then(res => {
              if (res) {
                setAdditionalEpisodes(
                  res.objects
                    .filter((item: ILightRailItem) => item.id + '' !== MEDIAID)
                    .map(railItemToLight)
                )
                setAdditionalDataFetched(true)
              }
            })
          }
        } catch (err) {
          console.error('Error fetching rail data:', err)
        }
      }
    }
  }, [
    playerProps.isTrailer,
    playerProps.isMovie,
    videoRailId,
    additionalDataFetched,
    ks,
    setAdditionalEpisodes,
    MEDIAID,
  ])

  useEffect(() => {
    if (playerProps) {
      setEpisodeDescription(playerProps.assetData?.description || '')
    }
  }, [playerProps, setEpisodeDescription])

  useEffect(() => {
    if (playerProps.isTrailer || playerProps.isMovie) {
      fetchSimilarPrograms(
        {
          type: playerProps.assetData?.type || 0,
          id: parseInt(MEDIAID),
          length: 0,
        },
        ks
      ).then(response => {
        if (response) {
          setSeasonsEpisodes(response.data.result.objects)
        }
      })
    }
  }, [MEDIAID, ks, playerProps])

  const crumbList = useMemo(() => {
    return [
      {
        name: playerProps?.assetData?.name,
      },
    ] as ICrumb[]
  }, [playerProps?.assetData?.name])

  useEffect(() => {
    if (PlayerWrapperRef.current) {
      const w = Math.round(PlayerWrapperRef.current.offsetWidth * 0.99)
      const h = Math.round((w * 9) / 16)
      // the background may already carry a size (it is replaced) or be empty
      setPoster(
        sizedOttImage(playerProps.background, w, h, 75) ??
          OTT_DEFAULT_IMAGE.desktop
      )
    }
  }, [playerProps.background])

  return (
    <>
      <BannerWrapper>
        <TopBanner isVod={true} key={'TopBanner'} />
      </BannerWrapper>
      <BreadCrumbs crumbList={crumbList} />
      <DynamicColumnsWrapper>
        <MainWrapper>
          <OttEditMe
            assetId={Number(MEDIAID)}
            type={'clip'}
            referenceId={playerProps.assetData?.referenceId}
          />
          <PlayerWrapper ref={PlayerWrapperRef}>
            {adDecision === 'block' ? (
              <BlockedAdsImage />
            ) : (
              <Player
                MEDIAID={MEDIAID}
                DURATION={playerProps.assetData?.duration || 0}
                isCatchup={!!playerProps.isCatchup}
                POSTER={poster}
              />
            )}
          </PlayerWrapper>
          <RailFirstItemDescriptionBellowPlayer>
            <MainRailCommits>
              <NameItemTextBellowPlayer>
                {playerProps.assetData?.name}
              </NameItemTextBellowPlayer>
              <CommentItemTextBellowPlayer>
                {playerProps.assetData?.description}
              </CommentItemTextBellowPlayer>
              <CommentItemDateBellowPlayer>
                {playerProps.assetData?.createDate
                  ? formatDate(String(playerProps.assetData.createDate))
                  : ''}
              </CommentItemDateBellowPlayer>
            </MainRailCommits>
          </RailFirstItemDescriptionBellowPlayer>
        </MainWrapper>
        <LeftColumn isMobile={isMobileDevice}>
          <OttTaboolaClipPageLeftRailWidget />
        </LeftColumn>
      </DynamicColumnsWrapper>
      {(playerProps.isTrailer || playerProps.isMovie) && (
        <CatchupProgramSeries>
          <TabsNavWrapper>
            {additionalEpisodes && additionalEpisodes.length > 0 && (
              <TabsNavItem
                onClick={() => {
                  selectedNav !== 'additional' && setSelectedNav('additional')
                  setTaboolaWidgetCounter(taboolaWidgetCounter + 1)
                }}
                theme={{
                  isSelected:
                    selectedNav === 'additional' ? 'primary' : 'default',
                }}
              >
                {he.additionalArticles}
              </TabsNavItem>
            )}
            <TabsNavItem
              onClick={() =>
                selectedNav !== 'similar' && setSelectedNav('similar')
              }
              theme={{
                isSelected: selectedNav === 'similar' ? 'primary' : 'default',
              }}
            >
              {he.similarShows}
            </TabsNavItem>
          </TabsNavWrapper>
          {seasonsEpisodes &&
            seasonsEpisodes.length > 0 &&
            selectedNav === 'similar' && (
              <ProgramSeries
                additionalSeries={seasonsEpisodes}
                taboolaCounter={taboolaCounter.current}
              />
            )}
          {additionalEpisodes && selectedNav === 'additional' && (
            <React.Fragment>
              <ProgramSeriesClip
                additionalSeries={additionalEpisodes}
                taboolaCounter={taboolaCounter.current}
                visibleRows={visibleRows}
                itemsPerRow={ITEMS_PER_ROW}
                currentAssetId={Number(MEDIAID)}
                isActiveTab={selectedNav === 'additional'}
                taboolaWidgetCounter={taboolaWidgetCounter}
                bigImage={true}
              />
              {visibleRows * ITEMS_PER_ROW < additionalEpisodes.length && (
                <ShowMoreButton
                  onClick={() => setVisibleRows(visibleRows + ADDITIONAL_ROWS)}
                >
                  <ShowMoreButtonText>{he.showMore}</ShowMoreButtonText>
                  <ShowMoreArrow />
                </ShowMoreButton>
              )}
            </React.Fragment>
          )}
        </CatchupProgramSeries>
      )}
    </>
  )
}
